import React from 'react'
import { useDispatch } from 'react-redux';
import { useState, useEffect } from 'react';
import api from '../../../axios/api';
import { Card, TextField, CardContent } from '@mui/material';
import CustomButton from '../../../componentsII/Buttons';
import { showNotification } from '../../../redux/slices/notificationSlice';
import { FormControl, MenuItem, Select } from '@mui/material';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const AddUpdateFuelEmissionModal = ({ onClose, refetch, fuelEmissionId = null, initialData = null }) => {

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const dispatch = useDispatch();

  const [fuelTypes, setFuelTypes] = useState([]);
  const [units, setUnits] = useState([]);
  
  const [selectedFuelForm, setSelectedFuelForm] = useState("");

  // Form state
  const [formData, setFormData] = useState({
    fuelTypeId: initialData?.fuel_id || '',
    unitId: initialData?.unit_id || '',
    emission: initialData?.emission_kgco2e || '',
    year: initialData?.valid_year ? new Date(initialData.valid_year, 0) : new Date(new Date().getFullYear(), 0),
  });

  const validate = (values) => {
    const newErrors = {};

    // Fuel Type validation
    if (!values.fuelTypeId) {
      newErrors.fuelTypeId = 'Fuel type is required';
    }

    // Unit validation
    if (!values.unitId) {
      newErrors.unitId = 'Unit is required';
    }

    // Emission validation
    if (!values.emission) {
      newErrors.emission = 'Emission value is required';
    } else if (isNaN(values.emission)) {
      newErrors.emission = 'Emission must be a number';
    } else if (Number(values.emission) < 0) {
      newErrors.emission = 'Emission cannot be negative';
    }


    // Year validation
    if (!values.year) {
      newErrors.year = 'Year is required';
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validate(formData);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        id: initialData?.id || 0,
        fuelTypeId: formData.fuelTypeId,
        unitId: formData.unitId,
        emission: formData.emission,
        year: formData.year ? formData.year.getFullYear() : null
      };

      const response = await api.post('/admin/CreateUpdateFuelEmission', payload);
      if (response.data.status == '200') {
        dispatch(showNotification({
          message: response.data.message,
          type: "success"
        }));
        onClose();
        refetch?.();
      } else {
        setErrors(prev => ({
          ...prev,
          general: response.data.message || 'Something went wrong'
        }));
      }
    } catch (error) {
      dispatch(showNotification({
        message: error.response.data.message,
        type: "ERROR"
      }));
      setErrors({ submit: error.message });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const getFuelTypes = async () => {
    try {
      const response = await api.get('/vehicle/getAllFuel?fuel_form=LIQUID&fuel_form=GAS');
      if (response.status == '200') {
        setFuelTypes(response.data.data);
        
        // If initialData exists, set the selectedFuelForm and fetch units
        if (initialData?.fuel_id) {
          const initialFuel = response.data.data.find(fuel => fuel.value === initialData.fuel_id);
          if (initialFuel) {
            setSelectedFuelForm(initialFuel.fuel_form);
            getFuelUnit(initialFuel.fuel_form);
          }
        }
      }
    } catch (error) {
      console.error(error);
    }
  }

  const getFuelUnit = async (fuelForm) => {
    try {
      // Determine which type_code to use based on fuel form
      let typeCode = "";
      if (fuelForm === "LIQUID") {
        typeCode = "LIQUID";
      } else if (fuelForm === "GAS") {
        typeCode = "VOLUME";
      } else if (fuelForm === "MASS") {
        typeCode = "MASS";
      } else {
        typeCode = "LIQUID,MASS,VOLUME"; // Fetch all if no specific form
      }
      
      const res = await api.get(`/common/measurementType?type_code=${typeCode}`);
      setUnits(res.data.data);
    } catch (error) {
      console.error("Error fetching units:", error);
    }
  };

  const handleFuelChange = (e) => {
    const fuelId = e.target.value;
    
    // Find the selected fuel object
    const selectedFuel = fuelTypes.find(fuel => fuel.value === fuelId);
    
    if (selectedFuel) {
      // Update the fuel form state
      setSelectedFuelForm(selectedFuel.fuel_form);
      
      // Update formData with new fuelTypeId and reset unitId
      setFormData(prev => ({
        ...prev,
        fuelTypeId: fuelId,
        unitId: '' // Reset unit selection when fuel changes
      }));
      
      // Clear unit error when fuel changes
      if (errors.unitId) {
        setErrors(prev => ({
          ...prev,
          unitId: ''
        }));
      }
      
      // Fetch appropriate units for the selected fuel form
      getFuelUnit(selectedFuel.fuel_form);
    }
  };

  useEffect(() => {
    getFuelTypes();
    // Initial units fetch will happen after getFuelTypes if initialData exists
    if (!initialData?.fuel_id) {
      getFuelUnit();
    }
  }, []);

  return (
    <Card className="w-full mx-auto">
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <label htmlFor="fuelTypeId" className="text-sm font-medium block">Fuel Type<span className="text-red-500">*</span></label>
            <FormControl fullWidth size="small">
              <Select
                labelId="fueltype-label"
                id="fuelTypeId"
                name="fuelTypeId"
                value={formData.fuelTypeId}
                onChange={handleFuelChange}
                disabled={isSubmitting}
                error={Boolean(errors.fuelTypeId)}
                className={'w-full'}
              >
                {fuelTypes.map((fuelType) => (
                  <MenuItem key={fuelType.value} value={fuelType.value}>
                    {fuelType.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {errors.fuelTypeId && (
              <p className="text-sm text-red-500 mt-1">{errors.fuelTypeId}</p>
            )}
          </div>

          <div className="space-y-2">
            <label htmlFor="unitId" className="text-sm font-medium block">Unit<span className="text-red-500">*</span></label>
            <FormControl fullWidth size="small">
              <Select
                labelId="unit-label"
                id="unitId"
                name="unitId"
                value={formData.unitId}
                onChange={handleInputChange}
                disabled={isSubmitting || !formData.fuelTypeId}
                error={Boolean(errors.unitId)}
                className={'w-full'}
              >
                {units.map((unit) => (
                  <MenuItem key={unit.id} value={unit.id}>
                    {unit.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {errors.unitId && (
              <p className="text-sm text-red-500 mt-1">{errors.unitId}</p>
            )}
          </div>

          <div className="space-y-2">
            <label htmlFor="emission" className="text-sm font-medium">
            kgCo2e Emission
              <span className="text-red-500">*</span>
            </label>
            <TextField
              size="small"
              id="emission"
              name="emission"
              value={formData.emission}
              onChange={handleInputChange}
              placeholder="Enter Emission Value kgCo2e"
              disabled={isSubmitting}
              error={Boolean(errors.emission)}
              className={'w-full'}
              type="number"
            />
            {errors.emission && (
              <p className="text-sm text-red-500 mt-1">{errors.emission}</p>
            )}
          </div>

          <div className="space-y-2">
            <label htmlFor="yearPicker" className="text-sm font-medium block">
              Select Year
              <span className="text-red-500">*</span>
            </label>
            <DatePicker
              id="yearPicker"
              selected={formData.year}
              onChange={(date) => handleInputChange({ target: { name: "year", value: date } })}
              showYearPicker
              dateFormat="yyyy"
              className="w-full border border-gray-300 rounded-md p-2 text-sm"
              wrapperClassName="w-full"
              disabled={isSubmitting}
            />
            {errors.year && <p className="text-sm text-red-500 mt-1">{errors.year}</p>}
          </div>

          <div className="flex justify-end gap-4 pt-4">
            <CustomButton
              className="bg-gray-500 hover:bg-gray-700 text-white"
              text="Cancel"
              click={onClose}
            />
            <CustomButton
              className="bg-green-600 hover:bg-green-700 text-white"
              text={fuelEmissionId ? "Update" : "Submit"}
              type="submit"
              disabled={isSubmitting}
            />
          </div>
        </form>
      </CardContent>
    </Card>
  )
}

export default AddUpdateFuelEmissionModal